import { useState, useRef, useCallback, useMemo } from 'react'
import {
  Rocket,
  ArrowRight,
  Sparkles,
  Loader2,
  FileText,
  Lightbulb,
  Upload,
  X,
  Check,
  AlertCircle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useDiscoveryStore, type AIAutoFill } from '@/stores/discoveryStore'
import { useAIStore } from '@/stores/aiStore'
import { callClaudeStreaming } from '@/lib/ai/claude'
import { useDocumentAnalyzer, type AnalysisResult } from '@/hooks/useDocumentAnalyzer'
import type { DetectedFacet } from '@/lib/api/client'

const AUTO_FILL_PROMPT = `You are a business analyst. Given a business idea or document content, infer:
- Customer segment (who is the target customer)
- Job-to-be-done (what they're trying to accomplish)
- Pain points (3 frustrations)
- Competitors (3 alternatives they use today)
- Product type (SaaS, Mobile App, Service, Marketplace, etc.)

Respond ONLY with valid JSON:
{
  "segment": "target customer description",
  "job": "main job customers are trying to do",
  "pains": ["pain 1", "pain 2", "pain 3"],
  "competitors": ["competitor 1", "competitor 2", "competitor 3"],
  "productType": "SaaS | Mobile App | Service | Marketplace | Physical Product"
}`

interface DiscoveryIntakeProps {
  onComplete: () => void
}

// Map facet types to discovery categories for display
const FACET_CATEGORY_MAP: Record<string, { label: string; color: string }> = {
  customer_segment: { label: 'Customer Segment', color: 'bg-business/20 text-business' },
  customer_pain: { label: 'Pain Point', color: 'bg-solution/20 text-solution' },
  problem: { label: 'Problem', color: 'bg-solution/20 text-solution' },
  job_to_be_done: { label: 'Job to be Done', color: 'bg-market/20 text-market' },
  competitor: { label: 'Competitor', color: 'bg-warm-100 text-warm-700' },
  feature: { label: 'Feature', color: 'bg-business/20 text-business' },
  value_proposition: { label: 'Value Prop', color: 'bg-market/20 text-market' },
}

export function DiscoveryIntake({ onComplete }: DiscoveryIntakeProps) {
  const { setIntakeComplete, setAIAutoFill, setExtractedEntities } = useDiscoveryStore()
  const { apiKey, isConfigured, coachingModel } = useAIStore()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [ideaSentence, setIdeaSentence] = useState('')
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [autoFill, setAutoFill] = useState<AIAutoFill | null>(null)
  const [autoFillLoading, setAutoFillLoading] = useState(false)
  const [autoFillError, setAutoFillError] = useState<string | null>(null)
  const [showFacets, setShowFacets] = useState(false)

  // Use unified document analyzer
  const {
    analyzeDocument,
    progress,
    source,
    error: analyzerError,
    isAnalyzing,
    reset: resetAnalyzer,
  } = useDocumentAnalyzer({
    onFacetsExtracted: (textFacets, visualFacets) => {
      console.log('[DiscoveryIntake] Facets extracted:', textFacets.length, visualFacets.length)
    },
    onAnalysisComplete: async (result) => {
      // Generate auto-fill summary after facets are extracted
      await generateAutoFillFromFacets(result)
    },
    onError: (err) => {
      console.error('[DiscoveryIntake] Analysis error:', err)
    },
  })

  // Get extracted facets from source
  const extractedFacets = useMemo(() => source?.facets || [], [source])

  // At least one input method must be used
  const hasIdea = ideaSentence.trim().length >= 5
  const hasUpload = uploadedFile !== null
  const hasAnalyzedContent = source !== null && extractedFacets.length > 0
  const canProceed = hasIdea || hasAnalyzedContent
  const canAnalyze = isConfigured && hasUpload && progress.stage === 'idle'
  const isProcessing = isAnalyzing || autoFillLoading

  // Generate auto-fill from facets
  const generateAutoFillFromFacets = useCallback(
    async (result: AnalysisResult) => {
      if (!apiKey) return

      setAutoFillLoading(true)
      setAutoFillError(null)

      try {
        // Build context from facets
        const facetContext = result.facets
          .map((f) => `[${f.facetTypeId}] ${f.content}`)
          .join('\n')

        let fullText = ''
        const inputContent = [
          hasIdea ? `Business idea: "${ideaSentence}"` : '',
          facetContext ? `Extracted insights:\n${facetContext}` : '',
        ]
          .filter(Boolean)
          .join('\n\n')

        await callClaudeStreaming(
          { apiKey, model: coachingModel, maxTokens: 1024 },
          AUTO_FILL_PROMPT,
          inputContent,
          (chunk) => {
            fullText += chunk
          }
        )

        const jsonMatch = fullText.match(/\{[\s\S]*\}/)
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]) as AIAutoFill
          setAutoFill(parsed)
          setAIAutoFill(parsed)
        }
      } catch (error) {
        console.error('Auto-fill failed:', error)
        setAutoFillError('Failed to generate summary. You can still proceed.')
      } finally {
        setAutoFillLoading(false)
      }
    },
    [apiKey, coachingModel, hasIdea, ideaSentence, setAIAutoFill]
  )

  // Generate auto-fill from idea only (when no file uploaded)
  const generateAutoFillFromIdea = useCallback(async () => {
    if (!apiKey || !hasIdea) return

    setAutoFillLoading(true)
    setAutoFillError(null)

    try {
      let fullText = ''
      await callClaudeStreaming(
        { apiKey, model: coachingModel, maxTokens: 1024 },
        AUTO_FILL_PROMPT,
        `Business idea: "${ideaSentence}"`,
        (chunk) => {
          fullText += chunk
        }
      )

      const jsonMatch = fullText.match(/\{[\s\S]*\}/)
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]) as AIAutoFill
        setAutoFill(parsed)
        setAIAutoFill(parsed)
      }
    } catch (error) {
      console.error('Auto-fill failed:', error)
      setAutoFillError('Failed to generate summary')
    } finally {
      setAutoFillLoading(false)
    }
  }, [apiKey, coachingModel, hasIdea, ideaSentence, setAIAutoFill])

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploadedFile(file)
    setAutoFill(null)
    setAutoFillError(null)
    resetAnalyzer()
  }

  const handleAnalyze = async () => {
    if (!canAnalyze || !uploadedFile) return

    await analyzeDocument({
      file: uploadedFile,
      title: uploadedFile.name,
      contentType: 'custom',
      sourceContext: 'discovery',
      tags: ['discovery-intake'],
    })
  }

  const removeFile = () => {
    setUploadedFile(null)
    setAutoFill(null)
    setAutoFillError(null)
    resetAnalyzer()
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleStart = () => {
    // Convert facets to legacy entity format for compatibility
    const legacyEntities = extractedFacets.map((f) => ({
      id: f.id,
      text: f.content,
      suggestedClass: {
        prefix: 'ispot',
        localName: f.facetTypeId,
        fullUri: `http://ispot.ai/ontology#${f.facetTypeId}`,
      },
      confidence: (f.confidence >= 0.8 ? 'high' : f.confidence >= 0.5 ? 'medium' : 'low') as
        | 'high'
        | 'medium'
        | 'low',
      evidence: f.evidence,
    }))

    if (legacyEntities.length > 0) {
      setExtractedEntities(legacyEntities)
    }

    setIntakeComplete({
      hasExistingBusiness: false,
      customerInterviews: 0,
      hasPitchDeck: hasUpload,
      knownCompetitors: autoFill?.competitors || [],
      hasValidatedPricing: false,
      isGreenfield: true,
      ideaSentence,
      sourceId: source?.id, // Pass the source ID for reference
    })
    onComplete()
  }

  const getFacetStyle = (facetTypeId: string) => {
    const category = Object.entries(FACET_CATEGORY_MAP).find(([key]) =>
      facetTypeId.toLowerCase().includes(key.toLowerCase())
    )
    return category ? category[1] : { label: facetTypeId, color: 'bg-muted text-muted-foreground' }
  }

  const error = analyzerError?.message || autoFillError

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="border-b pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-market/10 flex items-center justify-center">
            <Rocket className="w-5 h-5 text-market" />
          </div>
          <div>
            <CardTitle className="text-lg">Start Your Discovery</CardTitle>
            <p className="text-sm text-muted-foreground">
              Describe your idea or upload existing materials
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col p-6 overflow-auto">
        {/* Error Message */}
        {error && (
          <div className="mb-4 flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-solution/10 text-solution">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Option 1: Enter an Idea */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <div
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                hasIdea ? 'bg-market border-market' : 'border-border'
              }`}
            >
              {hasIdea && <Check className="w-3 h-3 text-white" />}
            </div>
            <Lightbulb className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">Enter your idea</span>
          </div>
          <textarea
            value={ideaSentence}
            onChange={(e) => {
              setIdeaSentence(e.target.value)
              // Reset auto-fill when idea changes
              if (autoFill) setAutoFill(null)
            }}
            placeholder="e.g., A platform that helps small restaurants manage their online orders..."
            rows={3}
            className="w-full p-3 rounded-lg border border-border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-market/50"
          />
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground">and/or</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        {/* Option 2: Upload Information */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <div
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                hasAnalyzedContent ? 'bg-market border-market' : 'border-border'
              }`}
            >
              {hasAnalyzedContent && <Check className="w-3 h-3 text-white" />}
            </div>
            <FileText className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">Upload information</span>
            {isConfigured && (
              <Badge className="bg-business/20 text-business text-[10px] ml-auto">
                <Sparkles className="w-3 h-3 mr-1" />
                AI Analysis
              </Badge>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,.md,.pdf,.doc,.docx"
            onChange={handleFileUpload}
            className="hidden"
          />

          {!uploadedFile ? (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full p-4 rounded-lg border-2 border-dashed border-border hover:border-market/50 hover:bg-market/5 transition-colors flex flex-col items-center gap-2"
            >
              <Upload className="w-5 h-5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Upload pitch deck, business plan, or notes
              </span>
              <span className="text-xs text-muted-foreground">
                {isConfigured
                  ? 'PDF, TXT, MD - AI will extract insights automatically'
                  : '.txt, .md, .pdf supported'}
              </span>
            </button>
          ) : (
            <div className="p-3 rounded-lg border border-border bg-warm-50">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-market flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{uploadedFile.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(uploadedFile.size / 1024).toFixed(1)} KB
                    {isAnalyzing && ` • ${progress.message || 'Processing...'}`}
                    {progress.stage === 'complete' &&
                      extractedFacets.length > 0 &&
                      ` • ${extractedFacets.length} insights found`}
                  </p>
                </div>
                {isProcessing ? (
                  <Loader2 className="w-4 h-4 animate-spin text-market" />
                ) : (
                  <button onClick={removeFile} className="p-1 hover:bg-warm-200 rounded">
                    <X className="w-4 h-4 text-muted-foreground" />
                  </button>
                )}
              </div>

              {/* Show extracted facets preview */}
              {extractedFacets.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border">
                  <button
                    onClick={() => setShowFacets(!showFacets)}
                    className="flex items-center gap-2 text-xs text-market hover:text-market-dark"
                  >
                    {showFacets ? (
                      <ChevronDown className="w-3 h-3" />
                    ) : (
                      <ChevronRight className="w-3 h-3" />
                    )}
                    <Sparkles className="w-3 h-3" />
                    {extractedFacets.length} insights extracted
                  </button>

                  {showFacets && (
                    <div className="mt-2 space-y-1 max-h-40 overflow-auto">
                      {extractedFacets.slice(0, 10).map((facet) => {
                        const style = getFacetStyle(facet.facetTypeId)
                        return (
                          <div
                            key={facet.id}
                            className="flex items-center gap-2 text-xs p-2 bg-white rounded"
                          >
                            <Badge className={`text-[9px] ${style.color}`}>{style.label}</Badge>
                            <span className="truncate">{facet.content}</span>
                          </div>
                        )
                      })}
                      {extractedFacets.length > 10 && (
                        <p className="text-xs text-muted-foreground pl-2">
                          +{extractedFacets.length - 10} more...
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* AI Analysis Button - Show when file uploaded but not analyzed */}
        {isConfigured && hasUpload && progress.stage === 'idle' && (
          <div className="mb-5">
            <Button variant="outline" onClick={handleAnalyze} disabled={isProcessing} className="w-full">
              <Sparkles className="w-4 h-4 mr-2" />
              Analyze with AI
            </Button>
          </div>
        )}

        {/* Generate Summary Button - Show when idea entered but no upload */}
        {isConfigured && hasIdea && !hasUpload && !autoFill && (
          <div className="mb-5">
            <Button
              variant="outline"
              onClick={generateAutoFillFromIdea}
              disabled={isProcessing}
              className="w-full"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Generate AI Summary
            </Button>
          </div>
        )}

        {/* AI Suggestions Section */}
        {autoFill && (
          <div className="flex-1 mb-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-market" />
              <span className="text-sm font-medium">AI Summary</span>
              <Badge className="bg-business/20 text-business text-[10px]">Ready</Badge>
            </div>

            <div className="p-4 rounded-lg bg-market/5 border border-market/20 space-y-3 text-sm">
              <div>
                <span className="text-xs text-muted-foreground uppercase">Target Segment</span>
                <p>{autoFill.segment}</p>
              </div>
              <div>
                <span className="text-xs text-muted-foreground uppercase">Job to be Done</span>
                <p>{autoFill.job}</p>
              </div>
              <div>
                <span className="text-xs text-muted-foreground uppercase">Pain Points</span>
                <ul className="list-disc list-inside">
                  {autoFill.pains.map((pain, i) => (
                    <li key={i}>{pain}</li>
                  ))}
                </ul>
              </div>
              <div>
                <span className="text-xs text-muted-foreground uppercase">Competitors</span>
                <p>{autoFill.competitors.join(', ')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Auto-fill loading indicator */}
        {autoFillLoading && !autoFill && (
          <div className="mb-5 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Generating summary...
          </div>
        )}

        {!isConfigured && canProceed && (
          <div className="mb-5 p-3 rounded-lg bg-warm-100 border border-warm-200">
            <p className="text-sm text-muted-foreground">
              <strong>Tip:</strong> Add your Claude API key in Settings for AI-powered insight
              extraction and suggestions.
            </p>
          </div>
        )}

        {/* Action Button */}
        <div className="pt-4 border-t mt-auto">
          <Button
            variant="market"
            className="w-full"
            onClick={handleStart}
            disabled={!canProceed || isProcessing}
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Start Discovery
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
          {!canProceed && (
            <p className="text-xs text-muted-foreground text-center mt-2">
              Enter an idea or upload a file to continue
            </p>
          )}
          {extractedFacets.length > 0 && (
            <p className="text-xs text-market text-center mt-2">
              <Sparkles className="w-3 h-3 inline mr-1" />
              {extractedFacets.length} insights will be available for import
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
